<script setup lang="ts" generic="T extends PropertyType">
import { PropertyType } from '@/backend/types'
import type { Field } from '@/modules/Project/Fields/types'
import { hasGroundingClaim } from '@/modules/Project/Fields/utils/grounding'
import FieldTooltipContent from '@/modules/Project/FieldTooltipContent.vue'
import FieldTooltipReasoningButton from '@/modules/Project/FieldTooltipReasoningButton.vue'
import type { Property } from '@/modules/Project/Properties/types'
import { useDotIndicator } from '@/modules/Project/useDotIndicator'
import { useFieldLock } from '@/modules/Project/useFieldLock'
import { useFieldRecompute } from '@/modules/Project/useFieldRecompute'
import { useFieldReset } from '@/modules/Project/useFieldReset'
import { useFieldStatusIndicator } from '@/modules/Project/useFieldStatusIndicator'
import { useFieldStatusIndicatorExtended } from '@/modules/Project/useFieldStatusIndicatorExtended'
import { useOpenGrounding } from '@/modules/Project/useOpenGrounding'
import SidebarFieldPropertyName from '@/sharedComponents/SidebarFieldPropertyName.vue'
import SidebarFieldToolControls from '@/sharedComponents/SidebarFieldToolControls.vue'
import SidebarHeaderSeparator from '@/sharedComponents/SidebarHeaderSeparator.vue'
import DotIndicator from '@/uiKit/DotIndicator.vue'
import type { IconName } from '@/uiKit/IconName'
import { computed, toRef, useId } from 'vue'

const props = defineProps<{ field: Field<T>; property: Property<T>; actionBarVisible?: boolean }>()

const labelId = useId()
const { isModelOutput } = useFieldStatusIndicator(computed(() => props.field))

const showDotIndicator = useDotIndicator(toRef(props, 'field'), toRef(props, 'property'))

const { valueInfo } = useFieldStatusIndicatorExtended(toRef(props, 'field'))

const trigger = computed<{ text: string; icon?: IconName; url?: string } | undefined>(() => {
  if (!valueInfo.value || valueInfo.value.type === 'user') {
    return undefined
  }

  if (valueInfo.value?.type === 'toolWithCorrection') {
    return {
      text: valueInfo.value.userName,
      icon: undefined,
      url: valueInfo.value.userAvatarUrl || undefined,
    }
  } else {
    return { text: valueInfo.value.toolLabel, icon: valueInfo.value.toolIcon, url: undefined }
  }
})

const reasoning = computed(() => {
  return props.field.type === PropertyType.single_select ||
    props.field.type === PropertyType.multi_select ||
    props.field.type === PropertyType.number ||
    props.field.type === PropertyType.reference
    ? ('metadata' in props.field && props.field.metadata?.motivation) || null
    : null
})

const { resetField } = useFieldReset()
const { toggleFieldLock } = useFieldLock()
const { openGrounding } = useOpenGrounding()
const { recomputeField } = useFieldRecompute()
</script>

<template>
  <div class="group flex flex-col">
    <div
      class="sticky top-0 flex h-7 flex-row items-center justify-between gap-1 bg-surface-secondary pr-1 pl-3 before:flex"
    >
      <SidebarFieldPropertyName
        :id="labelId"
        :name="property.name"
        :type="property.type"
        :is-model-output="isModelOutput"
      />
      <div class="flex shrink-0 grow flex-row items-center justify-end gap-1">
        <div
          class="group-focus-within:opacity-100 group-hover:opacity-100"
          :class="{ 'opacity-0': actionBarVisible !== true }"
        >
          <slot name="actions" />
        </div>

        <SidebarHeaderSeparator v-if="showDotIndicator || trigger || reasoning" />
        <FieldTooltipReasoningButton
          v-if="reasoning"
          :reasoning="reasoning"
        />
        <DotIndicator
          v-if="showDotIndicator"
          :reason="showDotIndicator"
          size="xs"
          variant="transparent"
          @recompute="recomputeField(field)"
        />
        <SidebarFieldToolControls
          v-if="trigger && valueInfo"
          :name="trigger.text"
          :url="trigger.url"
          :icon="trigger.icon"
        >
          <FieldTooltipContent
            class="z-1 mb-2.5 ml-1.5"
            :field-has-ground-truth="field.groundTruth"
            :reasoning="null"
            :value-info="valueInfo"
            :has-grounding-claim="hasGroundingClaim(field)"
            :property-type="property.type"
            :status="field.status"
            @open="openGrounding({ propertyId: property.id, entityId: field.entityId })"
            @reset="resetField(field)"
            @lock="toggleFieldLock(field)"
            @recompute="recomputeField(field)"
          />
        </SidebarFieldToolControls>
      </div>
    </div>

    <slot
      name="field"
      :label-id="labelId"
    />
  </div>
</template>
